import { Component } from '@theme/component';
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { changeCruxCartLine, fetchCruxCart, readCruxCart, confirmCruxCartView, matchingCruxLines } from '@theme/crux-cart-transactions';

import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

let cartEditorModule;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<boolean>>} */
  static #pendingCartOperations = new Set();

  static #checkoutHolds = 0;

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<boolean>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingCartOperations);
  }

  #debouncedQueue = debounce(() => this.#drainQuantityQueue(), 300);
  #quantityQueue = new Map();
  #queueRunning = false;
  #activeController;
  #latestCart;
  #editorListeners;
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #quantityChangePending = false;
  #pendingQuantityUpdates = 0;
  #cartUpdateUnconfirmed = false;
  #restoreController;
  #historyNavigationRefreshed = false;
  #refreshOnReconnect = false;
  #checkoutGuardGeneration = 0;
  /** @type {Map<HTMLElement, {ariaDisabled: string|null, ariaBusy: string|null, inert: boolean}>} */
  #checkoutControlStates = new Map();
  /** @type {Map<HTMLElement, boolean>} */
  #quantityControlStates = new Map();
  /** @type {{element: HTMLElement, key: string, rowIndex: number, selector: string}|null} */
  #quantityFocus = null;

  /** @param {Event} event */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Node)) return;
    if (!this.contains(event.target) || !event.detail.cartLine) return;
    if (this.#pendingQuantityUpdates > 0 || this.#cartUpdateUnconfirmed) return;
    this.#onQuantityChange(event);
  };

  #handleQuantityInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('input[data-cart-line]')) {
      this.#syncCheckoutBusy();
    }
  };

  #handleQuantityKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement) ||
      !event.target.matches('input[data-cart-line]')) return;
    event.preventDefault();
    event.stopPropagation();
    event.target.closest('cart-quantity-selector-component')?.setQuantity?.(event);
  };

  #enableScentEditors() {
    for (const editor of this.querySelectorAll('[data-crux-cart-editor][hidden]')) editor.removeAttribute('hidden');
  }

  #handleScentEditor = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const editor = target.closest('[data-crux-cart-editor]');
    if (!editor || !this.contains(editor)) return;
    if (event.type === 'click' && !target.closest('[data-crux-cart-save], [data-crux-cart-cancel], [data-crux-cart-retry]')) return;
    if (event.type === 'change' && !target.matches('[data-crux-cart-scent]')) return;
    if (event.type === 'toggle' && target !== editor) return;
    try {
      cartEditorModule ||= import('@theme/crux-cart-editor').catch((error) => {
        cartEditorModule = undefined;
        throw error;
      });
      const module = await cartEditorModule;
      if (this.isConnected && editor.isConnected && this.contains(editor)) {
        await module.handleCruxCartEditorEvent(event, this);
      }
    } catch {
      const message = editor.querySelector('[data-crux-cart-error]');
      if (message instanceof HTMLElement && editor.isConnected) {
        message.textContent = editor.dataset.changeFailedLabel || '';
        message.hidden = false;
        const retry = editor.querySelector('[data-crux-cart-retry]');
        if (retry instanceof HTMLElement) retry.hidden = false;
        const status = editor.querySelector('[data-crux-cart-status]');
        if (status instanceof HTMLElement) status.textContent = '';
      }
    }
  };

  /** @param {Event} event */
  #guardCheckout = (event) => {
    if (!this.#cartUpdateUnconfirmed && !this.#quantityChangePending && this.#pendingQuantityUpdates === 0) return;
    const target = event.target;
    const isCartSubmit = event.type === 'submit' && target instanceof HTMLFormElement && target.classList.contains('cart-form');
    const isCheckoutClick = event.type === 'click' && target instanceof Element &&
      target.closest('[name="checkout"], .additional-checkout-buttons');
    if (!isCartSubmit && !isCheckoutClick) return;
    if (!this.#cartUpdateUnconfirmed) {
      if (isCartSubmit) { this.holdCheckoutForQuantitySave(event); return; }
      if (target.closest('[name="checkout"]')) return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#syncCheckoutBusy();
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => this.#refreshAfterHistoryNavigation(event.persisted);

  #refreshAfterHistoryNavigation = async (persisted = false) => {
    const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
    const historyNavigation = CartItemsComponent.#isNavigationTiming(navigationEntry) && navigationEntry.type === 'back_forward';
    if (!persisted && (!historyNavigation || this.#historyNavigationRefreshed)) return;
    if (!this.dataset.sectionId) return;
    if (this.#quantityQueue.size || this.#pendingQuantityUpdates > 0) return;
    this.#historyNavigationRefreshed = true;
    const result = await this.refreshCart();
    if (result && this.isConnected) this.dispatchEvent(new CartSectionRestoredEvent());
  };

  #syncCheckoutBusy() {
    this.#quantityChangePending = this.#quantityQueue.size > 0 || [...this.querySelectorAll('input[data-cart-line]')]
      .some((input) => input instanceof HTMLInputElement && input.value !== input.defaultValue);
    const updating = this.#quantityChangePending || this.#pendingQuantityUpdates > 0;
    const busy = updating || this.#cartUpdateUnconfirmed;
    for (const [control, state] of this.#checkoutControlStates) {
      if (busy && this.contains(control)) continue;
      if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', state.ariaDisabled);
      if (state.ariaBusy === null) control.removeAttribute('aria-busy');
      else control.setAttribute('aria-busy', state.ariaBusy);
      control.inert = state.inert;
      this.#checkoutControlStates.delete(control);
    }
    if (busy) {
      for (const control of this.querySelectorAll('[name="checkout"], .additional-checkout-buttons')) {
        if (!(control instanceof HTMLElement)) continue;
        if (!this.#checkoutControlStates.has(control)) {
          this.#checkoutControlStates.set(control, {
            ariaDisabled: control.getAttribute('aria-disabled'),
            ariaBusy: control.getAttribute('aria-busy'),
            inert: control.inert,
          });
        }
        control.setAttribute('aria-busy', String(updating));
        if (control.matches('[name="checkout"]')) control.setAttribute('aria-disabled', 'true');
        if (control.matches('.additional-checkout-buttons')) control.inert = true;
      }
    }
    const status = this.querySelector('[data-cart-update-status]');
    if (status instanceof HTMLElement) {
      const message = updating ? status.dataset.updatingLabel || '' : '';
      status.dataset.updating = String(updating);
      if (status.textContent !== message) status.textContent = message;
    }
    this.#syncQuantityControls();
  }

  #syncQuantityControls() {
    const busy = this.#pendingQuantityUpdates > 0 || this.#cartUpdateUnconfirmed;
    for (const [control, wasInert] of this.#quantityControlStates) {
      if (busy && this.contains(control)) continue;
      control.inert = wasInert;
      this.#quantityControlStates.delete(control);
    }
    if (busy) {
      for (const control of this.querySelectorAll('.cart-items__quantity-controls, [data-crux-cart-editor]')) {
        if (!(control instanceof HTMLElement)) continue;
        if (!this.#quantityControlStates.has(control)) {
          this.#quantityControlStates.set(control, control.inert);
          const active = document.activeElement;
          if (!this.#quantityFocus && active instanceof HTMLElement && control.contains(active)) {
            const row = control.closest('[data-crux-line-quantity][data-key]');
            if (row instanceof HTMLElement) {
              const name = active.getAttribute('name');
              const selector = active instanceof HTMLInputElement ? 'input[data-cart-line]' :
                name === 'plus' || name === 'minus' ? `button[name="${name}"]` :
                control.matches('[data-crux-cart-editor]') ? '[data-crux-cart-editor] summary' : '.cart-items__remove';
              this.#quantityFocus = {
                element: active,
                key: row.dataset.key || '',
                rowIndex: (this.refs.cartItemRows || []).indexOf(row),
                selector,
              };
            }
          }
        }
        control.inert = true;
      }
      return;
    }
    const focus = this.#quantityFocus;
    this.#quantityFocus = null;
    if (!focus || !this.isConnected) return;
    const active = document.activeElement;
    if (active !== document.body && active !== document.documentElement && active !== focus.element) return;
    const rows = this.refs.cartItemRows || [];
    let row = rows.find((item) => item.dataset.key === focus.key);
    if (!row) {
      const indexedRow = rows[focus.rowIndex];
      if (indexedRow?.dataset.key?.split(':')[0] === focus.key.split(':')[0]) row = indexedRow;
    }
    if (!row && rows.length) row = rows[Math.min(Math.max(0, focus.rowIndex), rows.length - 1)];
    const target = row?.querySelector(focus.selector) || (!rows.length ? this.querySelector('.cart-items__empty-button') : null);
    if (target instanceof HTMLElement && !target.matches(':disabled') && !target.closest('[inert]')) {
      target.focus({ preventScroll: true });
    }
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    if (!event.promise) return;
    const external = this.#isExternalCartUpdate(event);
    this.#latestCart = undefined;
    const generation = this.#checkoutGuardGeneration;
    this.#pendingQuantityUpdates += 1;
    this.#syncCheckoutBusy();
    this.#disableCartItems();
    event.promise
      .then(async ({ detail }) => {
        if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
        if (detail?.cartState) {
          const result = { cart: detail.cartState, sections: detail.sections, needsReview: Boolean(detail.needsReview) };
          if (detail.renderedSections?.includes(this.sectionId)) confirmCruxCartView(this, result.cart);
          else Object.assign(result, await this.#presentCart(result, generation));
          this.#acceptCartResult(result);
          return;
        }
        if (detail?.renderedSections?.includes(this.sectionId)) {
          this.#enableScentEditors();
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full', injectStylesheet: this.isDrawer });
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
          if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, html, {
            mode: this.isDrawer ? 'hydration' : 'full',
            injectStylesheet: this.isDrawer,
          });
        }
        this.#enableScentEditors();
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(async (error) => {
        if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
        const controller = new AbortController();
        this.#restoreController = controller;
        try {
          const result = await this.#freshCartResult(controller.signal);
          const confirmed = await this.#presentCart(result, generation, controller.signal);
          this.#acceptCartResult(confirmed);
          this.#showCartNotice(this.dataset.updateRecoveredLabel || '');
          const refreshed = CartLinesUpdateEvent.createPromise();
          refreshed.promise.catch(() => {});
          this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: refreshed.promise }));
          this.#resolveCartEvent(refreshed, confirmed);
        } catch (recoveryError) {
          if (this.isConnected && generation === this.#checkoutGuardGeneration && !controller.signal.aborted) {
            this.#showUnconfirmedUpdate(recoveryError);
          }
        } finally {
          if (this.#restoreController === controller) this.#restoreController = undefined;
        }
      })
      .finally(() => {
        if (generation !== this.#checkoutGuardGeneration) return;
        this.#pendingQuantityUpdates = Math.max(0, this.#pendingQuantityUpdates - 1);
        this.#syncCheckoutBusy();
        this.#enableCartItems();
        if (this.#quantityQueue.size) this.#drainQuantityQueue();
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          const html = await this.#freshSection();
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
        }
        this.#enableScentEditors();
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('click', this.#guardCheckout, true);
    this.addEventListener('submit', this.#guardCheckout, true);
    this.addEventListener('input', this.#handleQuantityInput);
    this.addEventListener('keydown', this.#handleQuantityKeydown);
    this.#editorListeners = new AbortController();
    for (const type of ['toggle', 'change', 'click']) {
      this.addEventListener(type, this.#handleScentEditor, { capture: type === 'toggle', signal: this.#editorListeners.signal });
    }
    this.#enableScentEditors();
    this.#syncCheckoutBusy();
    if (this.#refreshOnReconnect) {
      this.#refreshOnReconnect = false;
      this.refreshCart();
    } else this.#refreshAfterHistoryNavigation();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enableScentEditors();
    if (this.#pendingQuantityUpdates > 0) this.#disableCartItems();
    this.#syncCheckoutBusy();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#refreshOnReconnect ||= this.#pendingQuantityUpdates > 0 || this.#quantityChangePending ||
      this.#quantityQueue.size > 0 || this.#cartUpdateUnconfirmed;
    window.removeEventListener('pageshow', this.#handlePageShow);
    if (this.#restoreController) this.#historyNavigationRefreshed = false;
    this.#restoreController?.abort();
    this.#restoreController = undefined;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('click', this.#guardCheckout, true);
    this.removeEventListener('submit', this.#guardCheckout, true);
    this.removeEventListener('input', this.#handleQuantityInput);
    this.removeEventListener('keydown', this.#handleQuantityKeydown);
    this.#editorListeners?.abort();
    this.#activeController?.abort();
    this.#activeController = undefined;
    this.#debouncedQueue.cancel();
    this.#quantityQueue.clear();
    this.#checkoutGuardGeneration += 1;
    this.#queueRunning = false;
    this.#latestCart = undefined;
    this.#quantityChangePending = false;
    this.#pendingQuantityUpdates = 0;
    this.#cartUpdateUnconfirmed = false;
    this.#syncCheckoutBusy();
    this.#enableCartItems();
  }

  /** @param {QuantitySelectorUpdateEvent} event */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    this.updateQuantity({ line, quantity, action: quantity === 0 ? 'clear' : 'change' });
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  #rowIdentity(row) {
    let properties = {};
    try {
      properties = JSON.parse(row.dataset.cartProperties || '{}') || {};
      if (Array.isArray(properties)) {
        if (!properties.every((entry) => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string')) return null;
        properties = Object.fromEntries(properties);
      }
    } catch { return null; }
    if (!properties || typeof properties !== 'object') return null;
    const variantId = row.dataset.cartVariantId || row.dataset.key?.split(':')[0] || '';
    if (!/^\d+$/.test(variantId)) return null;
    return { variantId, properties, sellingPlanId: row.dataset.cartSellingPlanId || '' };
  }

  #quantityCartState() {
    return this.#latestCart || {
      items: (this.refs.cartItemRows || []).flatMap((row) => {
        const identity = this.#rowIdentity(row);
        if (!identity) return [];
        return [{ key: row.dataset.key, variant_id: Number(identity.variantId), properties: identity.properties,
          selling_plan: identity.sellingPlanId, quantity: Number(row.dataset.cruxLineQuantity) }];
      }),
    };
  }

  /** @param {{line: number, quantity: number, action: string}} config */
  updateQuantity({ line, quantity, action }) {
    if (this.#cartUpdateUnconfirmed || this.#restoreController || this.#pendingQuantityUpdates > 0) return;
    const row = (this.refs.cartItemRows || [])[Number(line) - 1];
    const key = row?.dataset.key;
    const identity = row ? this.#rowIdentity(row) : null;
    if (!key || !identity || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const expectedQuantity = Number(row.dataset.cruxLineQuantity);
    const matching = matchingCruxLines(this.#quantityCartState(), identity);
    const expectedIdentityQuantity = matching.reduce((sum, item) => sum + item.quantity, 0);
    if (!Number.isSafeInteger(expectedQuantity) || expectedQuantity <= 0 ||
      !Number.isSafeInteger(expectedIdentityQuantity) || expectedIdentityQuantity < expectedQuantity) return;
    for (const editor of this.querySelectorAll('[data-crux-cart-editor][open]')) editor.open = false;
    this.#clearCartNotice();
    if (quantity === expectedQuantity) this.#quantityQueue.delete(key);
    else this.#quantityQueue.set(key, { key, quantity, identity, action,
      expectedQuantity, expectedIdentityQuantity, expectedIdentityLines: matching.length });
    this.#quantityChangePending = this.#quantityQueue.size > 0;
    this.#syncCheckoutBusy();
    this.#debouncedQueue.cancel();
    if (!this.#quantityQueue.size) return;
    if (quantity === 0) this.#drainQuantityQueue();
    else this.#debouncedQueue();
  }

  async #drainQuantityQueue() {
    if (this.#queueRunning || this.#pendingQuantityUpdates > 0 || this.#cartUpdateUnconfirmed || !this.isConnected) return;
    const generation = this.#checkoutGuardGeneration;
    this.#queueRunning = true;
    try {
      while (this.#quantityQueue.size && this.isConnected && !this.#cartUpdateUnconfirmed && generation === this.#checkoutGuardGeneration) {
        const [queuedKey, intent] = this.#quantityQueue.entries().next().value;
        this.#quantityQueue.delete(queuedKey);
        let key = intent.key;
        const matching = matchingCruxLines(this.#quantityCartState(), intent.identity);
        const expectedIdentityQuantity = matching.reduce((sum, item) => sum + item.quantity, 0);
        const exact = matching.find((item) => item.key === key);
        const equivalentSingleLine = intent.expectedIdentityLines === 1 && matching.length === 1 &&
          expectedIdentityQuantity === intent.expectedIdentityQuantity && matching[0].quantity === intent.expectedQuantity;
        const target = exact || (equivalentSingleLine ? matching[0] : null);
        if (!target) {
          if (intent.quantity !== 0 || matching.length > 0) this.#showCartNotice(this.dataset.itemChangedLabel || '');
          continue;
        }
        key = target.key;
        if (target.quantity === intent.quantity) continue;
        if (target.quantity !== intent.expectedQuantity) {
          this.#showCartNotice(this.dataset.itemChangedLabel || '');
          continue;
        }
        const marker = cartPerformance.createStartingMarker(`${intent.action}:user-action`);
        try {
          await this.#executeCartOperation(({ sections, sectionsUrl, signal }) => changeCruxCartLine({
            key, quantity: intent.quantity, identity: intent.identity,
            expectedQuantity: intent.expectedQuantity, expectedIdentityQuantity,
            changeUrl: Theme.routes.cart_change_url, cartUrl: `${Theme.routes.cart_url}.js`,
            sections, sectionsUrl, signal,
          }), {
            action: intent.quantity > 0 ? 'update' : 'remove',
            lines: [{ id: key, quantity: intent.quantity }],
          });
        } finally {
          cartPerformance.measureFromMarker(marker);
        }
      }
    } catch {
      if (generation === this.#checkoutGuardGeneration) this.#quantityQueue.clear();
    } finally {
      if (generation === this.#checkoutGuardGeneration) {
        this.#queueRunning = false;
        this.#quantityChangePending = this.#quantityQueue.size > 0;
        this.#syncCheckoutBusy();
        this.#enableCartItems();
      }
    }
  }

  #sectionIds() {
    return [...new Set([this.sectionId, ...[...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((item) => item.dataset.sectionId).filter(Boolean)])].slice(0, 5);
  }

  #assertCurrent(generation, signal) {
    if (!this.isConnected || generation !== this.#checkoutGuardGeneration || signal?.aborted) {
      throw new DOMException('Cart view changed before its update completed', 'AbortError');
    }
  }

  #cartView(section) {
    return [...section?.querySelectorAll('cart-items-component') || []]
      .find((element) => element.dataset.sectionId === this.sectionId);
  }

  #confirmSection(html, cart) {
    if (typeof html !== 'string' || !html.trim()) throw new Error('The cart section is missing.');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) throw new Error('The cart section wrapper is missing.');
    const view = confirmCruxCartView(this.#cartView(section), cart);
    if (this.isDrawer && !view.closest('[data-hydration-key="cart-drawer-inner"]')) {
      throw new Error('The cart drawer target is missing.');
    }
  }

  async #freshSection(signal) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', cancel, { once: true });
    const timer = setTimeout(cancel, 12000);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_crux_cart_refresh', String(Date.now()));
      return await sectionRenderer.getSectionHTML(this.sectionId, false, url, controller.signal);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    }
  }

  async #freshCartResult(signal) {
    const cart = await this.fetchCartData({ fresh: true, signal });
    const html = await this.#freshSection(signal);
    this.#confirmSection(html, cart);
    return { cart, sections: { [this.sectionId]: html }, recovered: true, changed: false };
  }

  async #presentCart(result, generation, signal) {
    let confirmed = { ...result, cart: readCruxCart(result.cart) };
    const present = async () => {
      this.#assertCurrent(generation, signal);
      const html = confirmed.sections?.[this.sectionId];
      this.#confirmSection(html, confirmed.cart);
      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full', injectStylesheet: true });
      this.#assertCurrent(generation, signal);
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      confirmCruxCartView(this.#cartView(section), confirmed.cart);
    };
    try {
      await present();
    } catch (error) {
      this.#assertCurrent(generation, signal);
      const refreshed = await this.#freshCartResult(signal);
      const quantities = new Map(confirmed.cart.items.map((item) => [item.key, item.quantity]));
      const sameCart = refreshed.cart.item_count === confirmed.cart.item_count &&
        refreshed.cart.total_price === confirmed.cart.total_price && refreshed.cart.currency === confirmed.cart.currency &&
        refreshed.cart.items.length === confirmed.cart.items.length &&
        refreshed.cart.items.every((item) => quantities.get(item.key) === item.quantity);
      confirmed = { ...confirmed, ...refreshed, changed: Boolean(confirmed.changed && sameCart),
        error: sameCart ? confirmed.error : '', errorCode: sameCart ? confirmed.errorCode : 'item_changed' };
      await present();
    }
    return confirmed;
  }

  /** @param {(options: {sections: string[], sectionsUrl: string, signal: AbortSignal}) => Promise<object>} operation */
  async runCartEdit(operation) {
    this.#syncCheckoutBusy();
    if (this.#quantityChangePending || this.#pendingQuantityUpdates > 0 || this.#cartUpdateUnconfirmed ||
      this.#queueRunning || document.documentElement.hasAttribute('data-crux-cart-guard')) {
      const error = new Error(this.dataset.cartBusyLabel || '');
      error.customerMessage = this.dataset.cartBusyLabel || '';
      throw error;
    }
    return this.#executeCartOperation(operation, { reviewOnRecovery: true });
  }

  async #executeCartOperation(operation, { action = 'update', lines = [], reviewOnRecovery = false } = {}) {
    const generation = this.#checkoutGuardGeneration;
    const controller = new AbortController();
    this.#activeController = controller;
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    let operationRejected = true;
    let completeCartOperation = () => {};
    /** @type {Promise<boolean>} */
    const cartOperation = new Promise((resolve) => { completeCartOperation = () => resolve(operationRejected); });
    CartItemsComponent.#pendingCartOperations.add(cartOperation);
    this.#pendingQuantityUpdates += 1;
    try {
      this.#clearCartNotice();
      this.#syncCheckoutBusy();
      this.#disableCartItems();
      this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
      this.refs.cartTotal?.shimmer();
      for (const sectionId of this.#sectionIds()) sectionRenderer.abortRender(sectionId);
      let result;
      try {
        result = await operation({ sections: this.#sectionIds(), sectionsUrl: window.location.pathname + window.location.search, signal: controller.signal });
      } catch (error) {
        this.#assertCurrent(generation, controller.signal);
        result = await this.#freshCartResult(controller.signal);
        result.needsReview = reviewOnRecovery;
        result.error = typeof error?.customerMessage === 'string' ? error.customerMessage : this.dataset.updateRecoveredLabel || '';
      }
      result = await this.#presentCart(result, generation, controller.signal);
      this.#assertCurrent(generation, controller.signal);
      this.#acceptCartResult(result);
      operationRejected = Boolean(result.needsReview || result.errorCode || this.#cartUpdateUnconfirmed);
      this.#resolveCartEvent(deferred, result);
      return result;
    } catch (error) {
      deferred.reject(error);
      if (this.isConnected && generation === this.#checkoutGuardGeneration && !controller.signal.aborted) {
        this.#showUnconfirmedUpdate(error);
      }
      throw error;
    } finally {
      try {
        if (this.#activeController === controller) this.#activeController = undefined;
        if (generation === this.#checkoutGuardGeneration) {
          resetShimmer(this);
          this.#pendingQuantityUpdates = Math.max(0, this.#pendingQuantityUpdates - 1);
          this.#syncCheckoutBusy();
          this.#enableCartItems();
        }
      } finally {
        CartItemsComponent.#pendingCartOperations.delete(cartOperation);
        completeCartOperation();
      }
    }
  }


  /** Wait for confirmed CRUX quantity/scents transactions before native checkout, as in Horizon 4.4.0. */
  holdCheckoutForQuantitySave = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !(event.submitter instanceof HTMLElement)) return;
    if (event.defaultPrevented) return;
    if (CartItemsComponent.#checkoutHolds || this.#cartUpdateUnconfirmed) {
      event.preventDefault();
      return;
    }
    const submitter = event.submitter;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && event.target.contains(active) && active.value !== active.defaultValue) active.blur();
    this.#debouncedQueue.flush();
    let pending = CartItemsComponent.pendingQuantityUpdates;
    if (!pending.length) return;
    event.preventDefault();
    let rejected = false;
    CartItemsComponent.#checkoutHolds++;
    try {
      while (pending.length) {
        rejected = (await Promise.all(pending)).some(Boolean) || rejected;
        this.#debouncedQueue.flush();
        pending = CartItemsComponent.pendingQuantityUpdates;
      }
    } finally {
      CartItemsComponent.#checkoutHolds--;
    }
    if (rejected || this.#cartUpdateUnconfirmed || !this.isConnected) return;
    const button = submitter.isConnected ? submitter : document.getElementById(submitter.id);
    if (button instanceof HTMLElement && !button.closest('[inert]') && !button.matches(':disabled')) button.click();
  };

  #acceptCartResult(result) {
    this.#latestCart = result.cart;
    this.#cartUpdateUnconfirmed = false;
    const requestError = this.querySelector('[data-cart-request-error]');
    if (requestError instanceof HTMLElement) requestError.hidden = true;
    this.#updateQuantitySelectors(result.cart);
    this.#updateCartQuantitySelectorButtonStates();
    this.#enableScentEditors();
    if (result.needsReview) {
      this.#showUnconfirmedUpdate(new Error('The cart requires review.'), true);
      return;
    }
    const label = result.errorCode === 'quantity_not_applied' ? this.dataset.quantityNotAppliedLabel :
      result.errorCode === 'scents_failed' ? this.dataset.scentsFailedLabel : this.dataset.itemChangedLabel;
    const message = result.error || (result.errorCode ? label : '');
    if (message) this.#showCartNotice(message);
  }

  #resolveCartEvent(deferred, result) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
      detail: { sections: result.sections, renderedSections: [this.sectionId], items: result.cart.items,
        itemCount: result.cart.item_count, source: 'cart-items-component', didError: false,
        cartState: result.cart, needsReview: Boolean(result.needsReview) },
    });
  }

  /** @param {Event} [event] */
  async refreshCart(event) {
    event?.preventDefault();
    if (this.#pendingQuantityUpdates > 0 || this.#queueRunning) return null;
    this.#debouncedQueue.cancel();
    this.#quantityQueue.clear();
    try {
      const result = await this.#executeCartOperation(({ signal }) => this.#freshCartResult(signal));
      if (event) this.#showCartNotice(this.dataset.updateRecoveredLabel || '');
      return result;
    } catch {
      return null;
    }
  }

  #showCartNotice(message) {
    const notice = this.querySelector('[data-cart-notice]');
    if (!(notice instanceof HTMLElement)) return;
    notice.textContent = message;
    notice.hidden = !message;
  }

  #clearCartNotice() {
    this.#showCartNotice('');
  }

  #showUnconfirmedUpdate(error, needsReview = false) {
    this.#cartUpdateUnconfirmed = true;
    for (const input of this.querySelectorAll('input[data-cart-line]')) {
      if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    }
    const requestError = this.querySelector('[data-cart-request-error]');
    if (requestError instanceof HTMLElement) requestError.hidden = false;
    const message = this.querySelector('[data-cart-request-error-message]');
    if (message instanceof HTMLElement) {
      message.textContent = needsReview ? this.dataset.reviewRequiredLabel || this.dataset.updateUnconfirmedLabel || '' :
        this.dataset.updateUnconfirmedLabel || '';
    }
    this.dispatchEvent(new CartErrorEvent({
      error: error?.message || 'Failed to update cart',
      code: 'SERVICE_UNAVAILABLE',
    }));
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this || !event.promise) return;
    this.#latestCart = undefined;
    const generation = this.#checkoutGuardGeneration;
    const peerUpdate = event.target instanceof CartItemsComponent;
    let needsReview = false;
    if (peerUpdate) {
      this.#pendingQuantityUpdates += 1;
      this.#syncCheckoutBusy();
      this.#disableCartItems();
    }

    event.promise
      .then(async ({ detail }) => {
        if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
        needsReview = Boolean(detail?.needsReview);
        if (peerUpdate && detail?.cartState) {
          const result = { cart: detail.cartState, sections: detail.sections, needsReview };
          if (detail.renderedSections?.includes(this.sectionId)) confirmCruxCartView(this, result.cart);
          else Object.assign(result, await this.#presentCart(result, generation));
          this.#acceptCartResult(result);
          return;
        }
        if (detail?.renderedSections?.includes(this.sectionId)) {
          this.#enableScentEditors();
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            await startViewTransition(() => morphSection(this.sectionId, cartItemsHtml, morphOptions), ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#enableScentEditors();
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          const html = await this.#freshSection();
          if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, html, morphOptions);
          this.#enableScentEditors();
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch(async (error) => {
        if (!this.isConnected || generation !== this.#checkoutGuardGeneration) return;
        if (!peerUpdate) {
          if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
          return;
        }
        const controller = new AbortController();
        this.#restoreController = controller;
        try {
          const result = await this.#freshCartResult(controller.signal);
          const confirmed = await this.#presentCart(result, generation, controller.signal);
          this.#acceptCartResult(confirmed);
          this.#showCartNotice(this.dataset.updateRecoveredLabel || '');
        } catch (recoveryError) {
          if (this.isConnected && generation === this.#checkoutGuardGeneration && !controller.signal.aborted) {
            this.#showUnconfirmedUpdate(recoveryError);
          }
        } finally {
          if (this.#restoreController === controller) this.#restoreController = undefined;
        }
      })
      .finally(() => {
        if (!peerUpdate || generation !== this.#checkoutGuardGeneration) return;
        if (needsReview && !this.#cartUpdateUnconfirmed) this.#showUnconfirmedUpdate(new Error('The cart requires review.'), true);
        this.#pendingQuantityUpdates = Math.max(0, this.#pendingQuantityUpdates - 1);
        this.#syncCheckoutBusy();
        this.#enableCartItems();
        if (this.#quantityQueue.size) this.#drainQuantityQueue();
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    if (this.#pendingQuantityUpdates === 0) this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // Discounts such as Buy X Get Y can split one variant across several cart lines.
    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData({ fresh = false, signal } = {}) {
    if (!fresh && this.#pendingCartFetch) return this.#pendingCartFetch;
    const request = fetchCruxCart({ cartUrl: `${Theme.routes.cart_url}.js`, signal });
    if (fresh) return request;
    this.#pendingCartFetch = request.finally(() => { this.#pendingCartFetch = null; });
    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
